import React, { createContext, useContext, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { adminTheme } from '../styles/adminTheme';

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Container = styled.div`
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ToastItem = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  min-width: 260px;
  max-width: 400px;
  animation: ${slideIn} 0.2s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  background: ${p =>
    p.$type === 'success' ? '#e6f4ea' :
    p.$type === 'error' ? '#fce8e6' :
    '#e8f0fe'};
  color: ${p =>
    p.$type === 'success' ? adminTheme.colors.success :
    p.$type === 'error' ? adminTheme.colors.danger :
    adminTheme.colors.primary};
`;

interface ToastMsg {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ToastContextValue {
  toast: (message: string, type?: 'success' | 'error' | 'info') => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {}, success: () => {}, error: () => {}, info: () => {} });

export const useToast = () => useContext(ToastContext);

let nextId = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);

  const toast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = nextId++;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);

  const success = useCallback((message: string) => toast(message, 'success'), [toast]);
  const error = useCallback((message: string) => toast(message, 'error'), [toast]);
  const info = useCallback((message: string) => toast(message, 'info'), [toast]);

  return (
    <ToastContext.Provider value={{ toast, success, error, info }}>
      {children}
      <Container>
        {toasts.map(t => (
          <ToastItem key={t.id} $type={t.type}>{t.message}</ToastItem>
        ))}
      </Container>
    </ToastContext.Provider>
  );
};
